/**
 * Upload Service
 * Uploads files to the Strapi media library using the admin token.
 * (Admin JWTs are accepted on the root /upload endpoint, not /api/upload.)
 */

import { API_CONFIG } from '../config/api.config';
import { storageService } from '../utils/storage';
import { performRenewToken } from './auth-permissions';

export interface UploadedFileInfo {
  id: number;
  documentId?: string;
  name: string;
  url: string;
  mime?: string;
  size?: number;
}

export async function uploadFiles(files: File[]): Promise<UploadedFileInfo[]> {
  if (!files || files.length === 0) return [];

  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const doUpload = () =>
    fetch(`${API_CONFIG.BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${storageService.getToken()}`,
      },
      body: formData,
    });

  let response = await doUpload();
  if (response.status === 401) {
    // Access cookie may have expired mid-session — renew and retry once
    try {
      await performRenewToken();
      response = await doUpload();
    } catch { /* fall through to the original 401 response */ }
  }

  if (!response.ok) {
    let message = 'File upload failed';
    try {
      const err = await response.json();
      message = err?.error?.message || message;
    } catch { /* keep default message */ }
    throw new Error(message);
  }

  const result = await response.json();
  const uploaded = Array.isArray(result) ? result : [result];
  return uploaded.map((f: any) => ({
    id: f.id,
    documentId: f.documentId,
    name: f.name,
    url: f.url,
    mime: f.mime,
    size: f.size,
  }));
}
